"use client";
import React, { JSX, startTransition, useContext, useOptimistic } from "react";

export const ActionContext = React.createContext<
  [boolean, (form: unknown) => Promise<void>]
>([false, () => Promise.resolve()]);

export function useAction<T>() {
  return useContext(ActionContext) as [boolean, (input: T) => Promise<void>];
}

type TransitionState = "pending" | "success" | "error" | "idle";

type TransitionContextValue = {
  states: { [id: string]: TransitionState };
  startTransition: (callback: () => Promise<void>, id?: string) => void;
};

export const TransitionContext =
  React.createContext<TransitionContextValue | null>(null);

export function useTransitionContext(
  id = "DEFAULT"
): [TransitionState, (callback: () => Promise<void>) => void] {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error(
      "useTransitionContext must be used within a TransitionProvider"
    );
  }
  return [
    context.states[id] || "idle",
    (callback: () => Promise<void>) => context.startTransition(callback, id),
  ];
}

export function TransitionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [states, setStates] = useOptimistic<{ [id: string]: TransitionState }>(
    {}
  );

  const handleStartTransition = (
    callback: () => Promise<void>,
    id = "DEFAULT"
  ) => {
    startTransition(async () => {
      setStates((prev) => ({
        ...prev,
        [id]: "pending",
      }));

      try {
        await callback();
        /*         setStates((prev) => ({
          ...prev,
          [id]: "success",
        })); */
      } catch {
        setStates((prev) => ({
          ...prev,
          [id]: "error",
        }));
      }
    });
  };

  return (
    <TransitionContext.Provider
      value={{
        states,
        startTransition: handleStartTransition,
      }}
    >
      {children}
    </TransitionContext.Provider>
  );
}

export const ActionFeedbackContext = React.createContext<
  [
    feedback: JSX.Element | undefined,
    (feedback: JSX.Element | undefined) => void
  ]
>([undefined, () => {}]);

export function useActionFeedback() {
  return useContext(ActionFeedbackContext);
}
